// packLayout — how the home desktop arranges itself.
//
// Folders on the free view are NOT placed by hand and their spots are never
// stored. Hand-placing was a standing bug factory: a fraction saved on one
// screen, replayed on another (or written NaN by a cancelled gesture), landed
// tiles in a heap in the top-left corner, and the spiral separator meant to
// catch that ran on positions which had already collapsed.
//
// So the room arranges itself, and it does so in a way that CANNOT collide:
// children walk into a cellular grid sized from the stage, and each tile is then
// offset inside ITS OWN cell by a deterministic per-key jitter whose budget is
// half the cell's slack over the required clearance. Two tiles in adjacent cells
// therefore can never approach closer than the clearance — collisions are
// impossible by construction, not by a search that might give up.
//
// Deterministic in the child's key, so a folder keeps its spot across renders,
// reloads and devices; a resize simply re-flows the room. See packLayout.test.ts
// for the invariant, exercised across counts and widths.

/** A tile is a 200px column of icon + label — it needs more air sideways than
 *  vertically. These are the guaranteed centre-to-centre clearances (px). */
export const SEP_X = 176;
export const SEP_Y = 132;
/** the room a row is given before jitter; the slack over the clearance is what
 *  the jitter is allowed to spend. */
export const CELL_X = 210;
export const CELL_Y = 158;

export interface Packed {
  /** centre positions per child key, as fractions of (stage width, `height`). */
  pos: Record<string, { x: number; y: number }>;
  /** the height the stage needs so every row has its air (px). */
  height: number;
}

/** deterministic 0..1 from a key — the jitter's only source of "randomness". */
export function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = (h ^ s.charCodeAt(i)) * 16777619;
  return ((h >>> 0) % 1000) / 1000;
}

export function packLayout(keys: string[], stageW: number, stageH: number): Packed {
  const n = keys.length;
  const pos: Record<string, { x: number; y: number }> = {};
  if (n === 0) return { pos, height: stageH };

  // side gutters so a tile's label never runs off the edge.
  const inset = Math.min(120, Math.max(52, stageW * 0.06));
  const usable = Math.max(CELL_X, stageW - inset * 2);
  const cols = Math.max(1, Math.min(n, Math.floor(usable / CELL_X)));
  const rows = Math.ceil(n / cols);

  // the cell the layout actually gets to spend (never below the clearance).
  const cellW = Math.max(SEP_X, usable / cols);
  const cellH = Math.max(SEP_Y, CELL_Y);

  // jitter budget = half the slack over the clearance, so an offset can never
  // carry a tile into a neighbour's guaranteed air.
  const jitterX = Math.max(0, (cellW - SEP_X) / 2);
  const jitterY = Math.max(0, (cellH - SEP_Y) / 2);

  const topPad = Math.max(96, stageH * 0.14);
  const height = Math.max(stageH, topPad + rows * cellH + 72);

  keys.forEach((key, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    // the last row centres its remainder rather than hanging left — a lone
    // trailing folder reads as placed, not as left over.
    const inRow = Math.min(cols, n - row * cols);
    const rowW = inRow * cellW;
    const x0 = (stageW - rowW) / 2 + (col + 0.5) * cellW;
    const y0 = topPad + (row + 0.5) * cellH;
    const x = x0 + (hash01(key + "x") - 0.5) * 2 * jitterX;
    const y = y0 + (hash01(key + "y") - 0.5) * 2 * jitterY;
    pos[key] = { x: x / Math.max(1, stageW), y: y / Math.max(1, height) };
  });

  return { pos, height };
}

export default packLayout;
